import React, { useEffect, useState } from 'react';
import { Outlet, useOutletContext, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { getUserProfile } from '../api/userProfile';
import MypageLayout from './layouts/MypageLayout';
import { ProfileStatus, ProfileTraffic } from './MypageNavbar';

export type MypageShellContext = {
  profileStatus: ProfileStatus;
  refreshProfileStatus: () => Promise<void>;
};

export function useMypageContext() {
  return useOutletContext<MypageShellContext>();
}

const digits = (s?: string | null) => String(s || '').replace(/\D/g, '');
const isYYYYMMDD = (s?: string | null) => /^\d{4}-\d{2}-\d{2}$/.test(String(s || ''));

const MypageShell: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [profileStatus, setProfileStatus] = useState<ProfileStatus>({ state: 'green', missing: [] });

  // WeChat 連携コールバック結果をトースト表示
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const bindWechat = params.get('bind_wechat');
    if (!bindWechat) return;
    if (bindWechat === 'ok') toast.success(t('profile.wechat_bind_success'));
    else if (bindWechat === 'already') toast.info('WeChat はすでに連携済みです');
    else if (bindWechat === 'conflict') toast.error('この WeChat は別アカウントに連携済みです');
    else toast.error('WeChat 連携に失敗しました');
    params.delete('bind_wechat');
    navigate({ search: params.toString() }, { replace: true });
  }, []);

  const refreshProfileStatus = async () => {
    try {
      const p = await getUserProfile();
      const missing: string[] = [];
      const fullName = String(p?.full_name || '').trim();
      const postal = digits(p?.postal_code);
      const addr = String(p?.address || '').trim();
      const gender = String(p?.gender || '').trim();
      const birth = String(p?.birth_date || '').trim();
      const phone = digits(p?.phone_number);

      if (!fullName.length) missing.push(t('profile.full_name') || '氏名');
      if (!/^\d{7}$/.test(postal)) missing.push(t('profile.postal_code') || '郵便番号（7桁）');
      if (!addr.length) missing.push(t('profile.address') || '住所');
      if (!gender.length) missing.push(t('profile.gender') || '性別');
      if (!isYYYYMMDD(birth)) missing.push(t('profile.birth_date') || '生年月日');
      if (!/^\d{10,13}$/.test(phone)) missing.push(t('profile.phone_number') || '電話番号');

      // 電話番号も「重要」に格上げ（無断キャンセル時などに患者へ連絡できるようにするため）
      const hasCritical = !fullName.length || !/^\d{7}$/.test(postal) || !addr.length || !/^\d{10,13}$/.test(phone);
      const state: ProfileTraffic = missing.length === 0 ? 'green' : hasCritical ? 'red' : 'yellow';
      setProfileStatus({ state, missing });
    } catch {
      setProfileStatus({ state: 'red', missing: [] });
    }
  };

  useEffect(() => {
    refreshProfileStatus();
  }, []);

  return (
    <MypageLayout title="マイページ" profileStatus={profileStatus}>
      <Outlet context={{ profileStatus, refreshProfileStatus } as MypageShellContext} />
    </MypageLayout>
  );
};

export default MypageShell;
